import { Dropdown, MenuProps, Tooltip } from 'antd';
import {
  DeleteOutlined,
  FileOutlined,
  FolderOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { DocumentView } from './DocumentView';
import { DocumentModal } from '@/utils/enums';
import TreeView from '@/components/folders/TreeView';
import styles from '@/styles/documents.module.css';
import { useTranslation } from 'react-i18next';
import { User } from '@/types';
import { canUploadFileToFolder, canUploadToFolder } from '@/utils/helpers';
import type { ReactNode } from 'react';

export default function LeftSidebar({
  navWidth,
  documentView,
  currentNode,
  expandedKeys,
  onExpand,
  onFolderNameClick,
  setOpenModal,
  setNodeToDelete,
  setCurrentNode,
  user,
  canEditDefault = false,
}: {
  navWidth: number;
  documentView: DocumentView;
  currentNode: any;
  expandedKeys: string[];
  onExpand: any;
  onFolderNameClick?: (node: any) => void;
  setOpenModal: any;
  setNodeToDelete?: (node: any) => void;
  setCurrentNode?: (node: any) => void;
  user: User | null;
  canEditDefault?: boolean;
}) {
  const { t } = useTranslation();

  const renderDisabledOption = (
    icon: ReactNode,
    label: string,
    tooltip: string,
  ) => (
    <Tooltip title={tooltip}>
      <span className={styles.dropdownOptions}>
        {icon}
        {label}
      </span>
    </Tooltip>
  );

  const buildAddItems = (
    targetNode: any,
    options?: { selectTarget?: boolean },
  ): MenuProps['items'] => {
    const canAddFolder = canUploadToFolder(user, targetNode);
    const canAddFile = canUploadFileToFolder(user, targetNode);
    const openAddModal = (modal: DocumentModal) => {
      if (options?.selectTarget && targetNode) {
        setCurrentNode?.(targetNode);
      }
      setOpenModal(modal);
    };

    return [
      {
        key: '1',
        disabled: !canAddFolder,
        label: canAddFolder ? (
          <div
            className={styles.dropdownOptions}
            onClick={() => {
              if (!canUploadToFolder(user, targetNode)) {
                return;
              }
              openAddModal(DocumentModal.ADD_FOLDER);
            }}
          >
            <FolderOutlined />
            {t('home.documents.addFolder')}
          </div>
        ) : (
          renderDisabledOption(
            <FolderOutlined />,
            t('home.documents.addFolder'),
            t('home.documents.addFolderDisabled'),
          )
        ),
      },
      {
        key: '2',
        disabled: !canAddFile,
        label: canAddFile ? (
          <div
            className={styles.dropdownOptions}
            onClick={() => {
              if (!canUploadFileToFolder(user, targetNode)) {
                return;
              }
              openAddModal(DocumentModal.ADD_FILE);
            }}
          >
            <FileOutlined />
            {t('home.documents.addFile')}
          </div>
        ) : (
          renderDisabledOption(
            <FileOutlined />,
            t('home.documents.addFile'),
            t('home.documents.addFileDisabled'),
          )
        ),
      },
    ];
  };

  const buildDeleteItem = (
    targetNode: any,
  ): NonNullable<MenuProps['items']>[number] => {
    const isDefaultRestricted = Boolean(targetNode?.default && !canEditDefault);
    const restrictedTooltip = t(
      'home.documents.table.actions.defaultFolderRestricted',
    );

    return {
      key: String(DocumentModal.DELETE),
      disabled: isDefaultRestricted,
      label: isDefaultRestricted ? (
        renderDisabledOption(
          <DeleteOutlined />,
          t('home.documents.table.actions.delete'),
          restrictedTooltip,
        )
      ) : (
        <div
          className={styles.dropdownOptions}
          onClick={() => {
            setNodeToDelete?.(targetNode);
            setOpenModal(DocumentModal.DELETE);
          }}
        >
          <DeleteOutlined />
          {t('home.documents.table.actions.delete')}
        </div>
      ),
    };
  };

  const getMenuItems = (
    isDefault: boolean = true,
    node: any,
  ): MenuProps['items'] => {
    const addItems =
      buildAddItems(isDefault ? currentNode : node, {
        selectTarget: !isDefault,
      }) ?? [];
    if (isDefault || node?.isLeaf) {
      return addItems;
    }
    return [...addItems, buildDeleteItem(node)];
  };

  return (
    <div>
      <div className={styles.leftSideBar}>
        <b>{t('home.documents.folderTitle')}</b>
        <Dropdown
          menu={{ items: buildAddItems(currentNode) }}
          trigger={['click']}
        >
          <div
            className={`${styles.dropdown} ${currentNode?.isLeaf ? 'disabled' : ''}`}
          >
            <PlusOutlined /> {t('home.documents.btnAdd')}
          </div>
        </Dropdown>
      </div>
      <TreeView
        width={navWidth}
        documentView={documentView}
        currentNode={currentNode}
        setOpenModal={setOpenModal}
        expandedKeys={expandedKeys}
        onExpand={onExpand}
        onFolderNameClick={onFolderNameClick}
        items={getMenuItems}
      />
    </div>
  );
}
